
import { useState, useEffect, useRef } from "react";
import { getAssetPath } from "@/utils/assetUtils";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";

interface LogoData {
  name: string;
  src: string;
  alt: string;
}

const PartnerLogos = () => {
  const [logosLoaded, setLogosLoaded] = useState<Record<string, boolean>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  
  const logos: LogoData[] = [
    { name: "nova", src: "Nova_Logo.png", alt: "Nova Logo" },
    { name: "insomniac", src: "Insomniac_Logo.png", alt: "Insomniac Logo" },
    { name: "patriots", src: "Patriots_Logo.png", alt: "Patriots Logo" },
    { name: "tao", src: "Tao_Logo.png", alt: "Tao Logo" },
    { name: "ln", src: "LN_Logo.png", alt: "Live Nation Logo" },
    { name: "wynn", src: "Wynn_Logo.png", alt: "Wynn Logo" },
    { name: "liv", src: "Liv_Logo.png", alt: "Liv Logo" },
    { name: "academy", src: "Academy_Logo.png", alt: "Academy Logo" },
  ];

  // Duplicate logos for seamless infinite scrolling
  const allLogos = [...logos, ...logos];

  const handleLogoLoad = (logoName: string) => {
    setLogosLoaded(prev => ({ ...prev, [logoName]: true }));
  };

  useEffect(() => {
    // Infinite scrolling animation
    const scrollContainer = containerRef.current;
    if (!scrollContainer) return;
    
    let animationId: number;
    let startTime: number | null = null;
    const duration = 30000; // 30 seconds for a complete cycle
    
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      
      // Calculate the scroll position based on time elapsed
      const progress = (elapsed % duration) / duration;
      const scrollWidth = scrollContainer.scrollWidth / 2;
      const scrollPosition = progress * scrollWidth;
      
      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <div className="w-full mt-14 mb-6">
      {/* Updated text callout - with font-bold added */}
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <h4 className="text-base md:text-lg text-white mb-3 font-bold text-center md:text-left">Bringing VIP Clients To</h4>
      </div>
      
      <div className="max-w-7xl mx-auto overflow-hidden">
        <div 
          ref={containerRef}
          className="flex items-center space-x-8 whitespace-nowrap overflow-x-hidden"
        >
          {allLogos.map((logo, index) => (
            <div 
              key={`${logo.name}-${index}`} 
              className="h-8 sm:h-10 inline-flex flex-shrink-0 items-center px-3"
            >
              <img
                src={getAssetPath(logo.src)}
                alt={logo.alt}
                className={`h-full w-auto object-contain max-w-[100px] transition-opacity duration-300 ${
                  logosLoaded[logo.name] ? "opacity-100" : "opacity-0"
                }`}
                onLoad={() => handleLogoLoad(logo.name)}
                onError={(e) => {
                  console.error(`Failed to load logo: ${logo.src}`, e);
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PartnerLogos;
